import React, { useEffect, useState } from 'react';
import { Card, CardHeader, CardContent, Avatar, Typography, Skeleton, Alert } from '@mui/material';
import { useAuth } from '../context/AuthContext';

/**
 * UserProfile component
 * Displays the current authenticated user's profile information.
 * Shows skeleton placeholders while loading and an error alert if fetch fails.
 */
const UserProfile = () => {
  const { user, loading: authLoading } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [profile, setProfile] = useState(null);

  useEffect(() => {
    const fetchProfile = async () => {
      if (!authLoading) {
        // Only fetch when auth check is complete and user exists
        if (!user) {
          setLoading(false);
          return;
        }
        try {
          console.log('[UserProfile] Initiating fetch to /api/user');
          setLoading(true);
          const response = await fetch('/api/user', {
            credentials: 'include'
          });
          if (!response.ok) {
            throw new Error(`Request failed: ${response.status}`);
          }
          const data = await response.json();
          console.log('[UserProfile] Received user data:', data.user);
          setProfile(data.user);
          setError(null);
        } catch (err) {
          setError(err.message);
        } finally {
          setLoading(false);
        }
      }
    };
    fetchProfile();
  }, [authLoading, user]);

  if (authLoading || loading) {
    return (
      <Card sx={{ width: '100%', mb: 3 }} data-testid="profile-skeleton">
        <CardHeader
          avatar={<Skeleton variant="circular" width={40} height={40} />}
          title={<Skeleton variant="text" width="40%" />}
          subheader={<Skeleton variant="text" width="60%" />}
        />
        <CardContent>
          <Skeleton variant="rectangular" height={118} />
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 3 }} data-testid="profile-error">
        Failed to load profile: {error}
      </Alert>
    );
  }

  if (!profile) {
    return null; // User not logged in
  }

  return (
    <Card sx={{ width: '100%', mb: 3 }} data-testid="user-profile">
      <CardHeader
        avatar={<Avatar src={profile.picture} alt={profile.name} />}
        title={profile.name}
        subheader={profile.email}
      />
      <CardContent>
        <Typography variant="body2" color="text.secondary">
          Welcome to your dashboard, {profile.given_name || profile.name}!
        </Typography>
      </CardContent>
    </Card>
  );
};

export default UserProfile;
